import React, { useState, useEffect, useContext } from 'react';
import {
  makeStyles,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme
} from '@material-ui/core';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  AttachMoney,
  People,
  Assessment,
  GetApp,
  Refresh,
  Store,
  Timeline,
  CompareArrows
} from '@material-ui/icons';
import {
  Line,
  Bar,
  Doughnut,
  Radar
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import { AuthContext } from '../../context/Auth/AuthContext';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';
import { i18n } from '../../translate/i18n';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import NappNotConfigured from '../../components/NappNotConfigured';
import useNappStatus from '../../hooks/useNappStatus';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  headerCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(3),
  },
  kpiCard: {
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  kpiIcon: {
    fontSize: '3rem',
    opacity: 0.8,
  },
  kpiValue: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    lineHeight: 1,
  },
  kpiGrowth: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  positiveGrowth: {
    color: theme.palette.success.main,
  },
  negativeGrowth: {
    color: theme.palette.error.main,
  },
  chartContainer: {
    height: 400,
    padding: theme.spacing(2),
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  exportButton: {
    margin: theme.spacing(0, 1),
  },
  platformChip: {
    margin: theme.spacing(0.5),
    '&.top-performer': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
  },
  comparisonCard: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const SalesManagement = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const nappStatus = useNappStatus();

  // Estados principais
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [salesMetrics, setSalesMetrics] = useState(null);
  const [platformComparison, setPlatformComparison] = useState([]);
  const [timeTrends, setTimeTrends] = useState([]);

  // Estados de filtros
  const [dateRange, setDateRange] = useState('30');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [comparisonPeriod, setComparisonPeriod] = useState('day');

  // Carregar dados iniciais
  useEffect(() => {
    loadSalesData();
  }, [dateRange, selectedPlatform, comparisonPeriod]);

  const loadSalesData = async () => {
    if (!user?.companyId) return;

    setLoading(true);
    try {
      const dateFrom = subDays(new Date(), parseInt(dateRange));
      const dateTo = new Date();

      const params = {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        ...(selectedPlatform !== 'all' && { platformName: selectedPlatform }),
      };

      // Buscar dados em paralelo
      const [kpisRes, metricsRes, platformsRes, trendsRes] = await Promise.all([
        api.get('/orders/analytics/kpis', { params }),
        api.get('/orders/analytics/metrics', { params }),
        api.get('/orders/analytics/platforms', { params }),
        api.get('/orders/analytics/trends', { params: { ...params, period: comparisonPeriod } }),
      ]);

      setKpis(kpisRes.data.data);
      setSalesMetrics(metricsRes.data.data);
      setPlatformComparison(platformsRes.data.data);
      setTimeTrends(trendsRes.data.data);

    } catch (error) {
      console.error('Erro ao carregar dados de vendas:', error);
      toastUtils.error('Erro ao carregar dados de vendas');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSalesData();
    setRefreshing(false);
    toastUtils.success('Dados atualizados com sucesso');
  };

  const handleExport = async (format = 'json') => {
    try {
      const dateFrom = subDays(new Date(), parseInt(dateRange));
      const dateTo = new Date();

      const params = {
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
        format,
        type: 'full',
        ...(selectedPlatform !== 'all' && { platformName: selectedPlatform }),
      };

      const response = await api.get('/orders/analytics/export', { params });

      // Criar download do arquivo
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: format === 'json' ? 'application/json' : 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-vendas-${format(new Date(), 'yyyy-MM-dd')}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toastUtils.success('Relatório exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toastUtils.error('Erro ao exportar relatório');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const renderKpiCard = (title, value, growth, icon, format = 'currency') => {
    const isPositive = growth >= 0;
    const formattedValue = format === 'currency' ? formatCurrency(value) :
                          format === 'percent' ? formatPercent(value) :
                          format === 'number' ? value.toLocaleString('pt-BR') : value;

    return (
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.kpiCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography className={classes.metricLabel}>
                  {title}
                </Typography>
                <Typography className={classes.kpiValue} color="textPrimary">
                  {formattedValue}
                </Typography>
                <Box className={classes.kpiGrowth}>
                  {isPositive ? (
                    <TrendingUp className={classes.positiveGrowth} />
                  ) : (
                    <TrendingDown className={classes.negativeGrowth} />
                  )}
                  <Typography
                    variant="body2"
                    className={isPositive ? classes.positiveGrowth : classes.negativeGrowth}
                  >
                    {formatPercent(Math.abs(growth))}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.kpiIcon} color="action">
                {icon}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Configuração dos gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const trendsChartData = {
    labels: timeTrends.map(t => format(new Date(t.date), 'dd/MM', { locale: ptBR })),
    datasets: [
      {
        label: 'Pedidos',
        data: timeTrends.map(t => t.orders),
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}20`,
        fill: true,
      },
      {
        label: 'Receita',
        data: timeTrends.map(t => t.revenue),
        borderColor: theme.palette.secondary.main,
        backgroundColor: `${theme.palette.secondary.main}20`,
        yAxisID: 'y1',
      },
    ],
  };

  const platformChartData = {
    labels: platformComparison.map(p => p.platform),
    datasets: [
      {
        label: 'Pedidos',
        data: platformComparison.map(p => p.orders),
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.success.main,
        ],
      },
    ],
  };

  // Se não tem permissão para NAPP
  if (!nappStatus.hasPermission) {
    return (
      <MainContainer className={classes.root}>
        <Paper className={classes.mainPaper} elevation={1}>
          <Box p={3} textAlign="center">
            <Typography variant="h5" gutterBottom>
              Recurso Não Disponível
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Este recurso não está disponível no seu plano atual.
            </Typography>
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  // Se ainda está carregando a verificação
  if (nappStatus.isLoading) {
    return (
      <MainContainer className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  // Se não está configurado ou precisa de configuração
  if (!nappStatus.isConfigured || nappStatus.needsConfiguration) {
    return <NappNotConfigured pageType="sales" />;
  }

  if (loading && !kpis) {
    return (
      <MainContainer className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>{i18n.t('sales.management.title', 'Central de Vendas')}</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={1}>
        {/* Header com informações gerais */}
        <Card className={classes.headerCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" component="h1">
                  {i18n.t('sales.management.title', 'Central de Vendas')}
                </Typography>
                <Typography variant="body1" style={{ opacity: 0.9 }}>
                  {i18n.t('sales.management.subtitle', 'Gestão Avançada de Performance de Vendas')}
                </Typography>
              </Box>
              <Box>
                <Tooltip title="Atualizar dados">
                  <IconButton
                    color="inherit"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Filtros */}
        <Paper className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Período</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Período"
                >
                  <MenuItem value="7">Últimos 7 dias</MenuItem>
                  <MenuItem value="30">Últimos 30 dias</MenuItem>
                  <MenuItem value="90">Últimos 90 dias</MenuItem>
                  <MenuItem value="365">Último ano</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Plataforma</InputLabel>
                <Select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  label="Plataforma"
                >
                  <MenuItem value="all">Todas</MenuItem>
                  {platformComparison.map(platform => (
                    <MenuItem key={platform.platform} value={platform.platform}>
                      {platform.platform}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Agrupamento</InputLabel>
                <Select
                  value={comparisonPeriod}
                  onChange={(e) => setComparisonPeriod(e.target.value)}
                  label="Agrupamento"
                >
                  <MenuItem value="day">Diário</MenuItem>
                  <MenuItem value="week">Semanal</MenuItem>
                  <MenuItem value="month">Mensal</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  onClick={() => handleExport('json')}
                  className={classes.exportButton}
                >
                  JSON
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  onClick={() => handleExport('csv')}
                  className={classes.exportButton}
                  disabled
                >
                  CSV
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {kpis && (
          <>
            {/* KPIs Executivos */}
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
              {renderKpiCard(
                'Receita Total',
                kpis.totalRevenue.value,
                kpis.totalRevenue.growth,
                <AttachMoney />,
                'currency'
              )}
              {renderKpiCard(
                'Total de Pedidos',
                kpis.totalOrders.value,
                kpis.totalOrders.growth,
                <ShoppingCart />,
                'number'
              )}
              {renderKpiCard(
                'Ticket Médio',
                kpis.averageOrderValue.value,
                kpis.averageOrderValue.growth,
                <Assessment />,
                'currency'
              )}
              {renderKpiCard(
                'Taxa de Conversão',
                kpis.conversionRate.value,
                kpis.conversionRate.growth,
                <TrendingUp />,
                'percent'
              )}
            </Grid>

            {/* Gráficos Principais */}
            <Grid container spacing={3} style={{ marginBottom: 24 }}>
              {/* Tendências Temporais */}
              <Grid item xs={12} lg={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <Timeline />
                      Tendências de Vendas
                    </Typography>
                    <Box className={classes.chartContainer}>
                      <Line data={trendsChartData} options={chartOptions} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance por Plataforma */}
              <Grid item xs={12} lg={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <Store />
                      Vendas por Plataforma
                    </Typography>
                    <Box className={classes.chartContainer}>
                      <Doughnut data={platformChartData} options={{ ...chartOptions, scales: undefined }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Análise Comparativa de Plataformas */}
            <Card>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  <CompareArrows />
                  Análise Comparativa de Plataformas
                </Typography>

                <Grid container spacing={2}>
                  {platformComparison.map((platform, index) => (
                    <Grid item xs={12} sm={6} md={4} key={platform.platform}>
                      <Card className={classes.comparisonCard} variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">{platform.platform}</Typography>
                            {index === 0 && (
                              <Chip
                                label="Top Performer"
                                size="small"
                                className={`${classes.platformChip} top-performer`}
                              />
                            )}
                          </Box>

                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography className={classes.metricLabel}>
                                Pedidos
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {platform.orders.toLocaleString('pt-BR')}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.metricLabel}>
                                Receita
                              </Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {formatCurrency(platform.revenue)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.metricLabel}>
                                Ticket Médio
                              </Typography>
                              <Typography variant="body2">
                                {formatCurrency(platform.averageOrderValue)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography className={classes.metricLabel}>
                                Conversão
                              </Typography>
                              <Typography variant="body2">
                                {formatPercent(platform.conversionRate)}
                              </Typography>
                            </Grid>
                          </Grid>

                          <Box mt={1}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {platform.growthRate >= 0 ? (
                                <TrendingUp className={classes.positiveGrowth} fontSize="small" />
                              ) : (
                                <TrendingDown className={classes.negativeGrowth} fontSize="small" />
                              )}
                              <Typography
                                variant="caption"
                                className={platform.growthRate >= 0 ? classes.positiveGrowth : classes.negativeGrowth}
                              >
                                {formatPercent(Math.abs(platform.growthRate))} vs período anterior
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default SalesManagement;